import { type ReactNode } from 'react';
import * as stylex from '@stylexjs/stylex';

export { describeError } from './describe-error';

const styles = stylex.create({
  wrap: {
    display: 'grid',
    placeItems: 'center',
    minHeight: '40vh',
    padding: '2rem',
    gap: '0.75rem',
  },
  title: {
    fontSize: '1.125rem',
    fontWeight: 600,
    margin: 0,
  },
  msg: { fontSize: '0.875rem', opacity: 0.7, maxWidth: '36rem', textAlign: 'center' },
  btn: {
    padding: '0.5rem 1rem',
    borderRadius: '0.5rem',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'currentColor',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    opacity: { default: 1, ':hover': 0.85 },
  },
});

export interface ErrorFallbackProps {
  title?: ReactNode;
  message?: ReactNode;
  retryLabel?: string;
  onRetry?: () => void;
}

/**
 * Generic error UI shown by router-level `defaultErrorComponent` and the
 * per-route lazy-load ErrorBoundary. Used by all three SPAs.
 *
 * Intentionally a presentational primitive: it does NOT call `useTranslation`
 * so it remains safe to mount before i18n is initialized (router boot, chunk
 * load failure, SSR/early-error paths). Callers pass already-translated
 * strings.
 */
export function ErrorFallback({
  title,
  message,
  retryLabel = 'Try again',
  onRetry,
}: ErrorFallbackProps) {
  return (
    <div {...stylex.props(styles.wrap)} role="alert" aria-live="assertive">
      <h2 {...stylex.props(styles.title)}>{title ?? 'Something went wrong'}</h2>
      <span {...stylex.props(styles.msg)}>
        {message ?? 'A page resource failed to load. Reload to try again.'}
      </span>
      <button
        type="button"
        {...stylex.props(styles.btn)}
        onClick={onRetry ?? (() => window.location.reload())}
      >
        {retryLabel}
      </button>
    </div>
  );
}
